import { NotificationTypeIcon } from "@/components/ui/notification-type-icon";
import { Button } from "@/components/ui/button";
import type { INotification } from "@/interfaces/notification.interface";

export interface NotificationPopoverContentProps {
  notifications: INotification[];
  isLoading?: boolean;
  onClearAll?: () => void;
  onViewAll?: () => void;
}

export function NotificationPopoverContent({
  notifications,
  isLoading = false,
  onClearAll,
  onViewAll,
}: NotificationPopoverContentProps) {
  // Show only the 3 most recent notifications
  const recentNotifications = notifications.slice(0, 3);

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Agora";
    if (diffInHours === 1) return "1 hora atrás";
    if (diffInHours < 24) return `${diffInHours} horas atrás`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 dia atrás";
    return `${diffInDays} dias atrás`;
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">
          <p className="text-gray-400 text-sm">Carregando notificações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-600">
        <h3 className="font-['Plus_Jakarta_Sans'] font-bold text-white text-[16px]">
          Notificações ({recentNotifications.length.toString().padStart(2, '0')})
        </h3>
        {onClearAll && recentNotifications.length > 0 && (
          <button
            onClick={onClearAll}
            className="font-['Plus_Jakarta_Sans'] font-normal text-[#A085FF] text-[12px] underline hover:text-[#B096FF] transition-colors"
          >
            Limpar todos
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-h-[400px] overflow-y-auto">
        {recentNotifications.length > 0 ? (
          <div className="space-y-0">
            {recentNotifications.map((notification, index) => (
              <div key={notification.id}>
                <div className="flex gap-3 items-start px-4 py-3 hover:bg-white/5 transition-colors">
                  {/* Notification Type Icon */}
                  <div className="shrink-0 mt-1">
                    <NotificationTypeIcon type={notification.type} size="sm" />
                  </div>

                  {/* Notification Content */}
                  <div className="flex-1 min-w-0">
                    <p className="font-['Plus_Jakarta_Sans'] font-semibold text-white text-[14px] leading-normal">
                      {notification.message}
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal text-[#888888] text-[12px] leading-normal mt-1">
                      {getTimeAgo(notification.createdAt ?? "")}
                    </p>
                  </div>

                  {/* Unread Indicator */}
                  {!notification.read && (
                    <div className="shrink-0">
                      <div className="size-2 rounded-full bg-[#7846FF]" />
                    </div>
                  )}
                </div>

                {/* Divider (except for last item) */}
                {index < recentNotifications.length - 1 && (
                  <div className="h-px bg-gray-700/50 mx-4" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 px-4">
            <p className="text-gray-400 text-sm">Nenhuma notificação recente</p>
          </div>
        )}
      </div>

      {/* Footer Link */}
      {onViewAll && recentNotifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-600">
          <Button
            onClick={onViewAll}
            variant="ghost"
            className="w-full font-['Plus_Jakarta_Sans'] font-normal text-[#A085FF] text-[14px] hover:text-[#B096FF] hover:bg-white/5"
          >
            Ver todas notificações
          </Button>
        </div>
      )}
    </div>
  );
}
